import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import auth from "../services/auth";
import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Business,
  Chat,
  Logout,
  ArrowBack,
  Home,
  CalendarToday,
  Handyman,
  Archive,
} from "@mui/icons-material";

import Documents from "../components/Documents";
import Contract from "../components/Contract";
import Servicos from "../components/Service";
import Arquivo from "../components/Arquivo";
import Events from "../components/Events";
import { getChatLockStatus, getChatUnreadCount } from "../services/source";

const ClientDashboardScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authUser = auth.getCurrentUser();

  const selectedClient =
    location.state?.client || authUser?.data || authUser || {};
  const empresa =
    selectedClient?.empresa || authUser?.data?.empresa || "utilizador";
  const clientName =
    selectedClient?.nome || selectedClient?.name || empresa || "Cliente";
  const principalClientId =
    authUser?.data?.clientRootId ||
    selectedClient?.clientRootId ||
    location.state?.client?.id ||
    selectedClient?.id ||
    selectedClient?.cliente_id ||
    selectedClient?.clienteId ||
    selectedClient?.uuid ||
    authUser?.data?.id ||
    authUser?.data?.cliente_id ||
    null;
  const sessionToken = authUser?.data?.sessionToken || "";
  const userType = authUser?.data?.type || "";
  const resolvedCompanyId =
    selectedClient?.companyId ||
    selectedClient?.company_id ||
    authUser?.data?.companyId ||
    authUser?.data?.id ||
    null;
  const [chatLocked, setChatLocked] = useState(false);
  const [chatLockLoading, setChatLockLoading] = useState(false);
  const [chatLockMessage, setChatLockMessage] = useState("");
  const [chatAlertOpen, setChatAlertOpen] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [activeSection, setActiveSection] = useState("home");
  const topSectionRef = useRef(null);
  const servicesSectionRef = useRef(null);
  const eventsSectionRef = useRef(null);

  const refreshChatLockStatus = useCallback(async () => {
    if (
      !resolvedCompanyId ||
      !principalClientId ||
      !sessionToken ||
      !userType
    ) {
      setChatLocked(false);
      setChatLockMessage("");
      return;
    }

    try {
      setChatLockLoading(true);
      const status = await getChatLockStatus({
        company_id: resolvedCompanyId,
        client_id: principalClientId,
        session_token: sessionToken,
        user_type: userType,
      });

      setChatLocked(Boolean(status?.locked));
      setChatLockMessage(status?.message || "");
    } catch (error) {
      console.error("Error fetching chat lock status:", error);
      setChatLocked(false);
      setChatLockMessage("");
    } finally {
      setChatLockLoading(false);
    }
  }, [resolvedCompanyId, principalClientId, sessionToken, userType]);

  useEffect(() => {
    refreshChatLockStatus();
  }, [refreshChatLockStatus]);

  useEffect(() => {
    const handleFocus = () => {
      refreshChatLockStatus();
    };

    const intervalId = window.setInterval(refreshChatLockStatus, 15000);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refreshChatLockStatus]);

  const refreshChatUnread = useCallback(async () => {
    if (
      !resolvedCompanyId ||
      !principalClientId ||
      !authUser?.data?.id ||
      !userType
    ) {
      setChatUnreadCount(0);
      return;
    }

    try {
      const res = await getChatUnreadCount({
        company_id: resolvedCompanyId,
        client_id: principalClientId,
        user_id: authUser.data.id,
        user_type: userType,
      });

      setChatUnreadCount(Number(res?.data?.unread_count || 0));
    } catch (error) {
      console.error("Error fetching unread chat count:", error);
      setChatUnreadCount(0);
    }
  }, [resolvedCompanyId, principalClientId, authUser?.data?.id, userType]);

  useEffect(() => {
    refreshChatUnread();
  }, [refreshChatUnread]);

  useEffect(() => {
    const intervalId = window.setInterval(refreshChatUnread, 5000);
    return () => window.clearInterval(intervalId);
  }, [refreshChatUnread]);

  const goToChat = () => {
    if (chatLocked || chatLockLoading) {
      setChatAlertOpen(true);
      return;
    }

    navigate("/chat", {
      state: {
        client: selectedClient || {
          nome: clientName,
          empresa,
          company: principalClientId,
        },
      },
    });

    setChatUnreadCount(0);
  };

  const scrollToSection = (sectionName) => {
    if (sectionName === "services") {
      setActiveSection("services");

      window.requestAnimationFrame(() => {
        servicesSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });

      return;
    }

    if (sectionName === "files") {
      setActiveSection("files");

      window.setTimeout(() => {
        topSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 0);

      return;
    }

    setActiveSection(sectionName === "events" ? "events" : "home");

    window.setTimeout(() => {
      const targetRef =
        sectionName === "events" ? eventsSectionRef : topSectionRef;

      targetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  const voltar = authUser?.data?.type === "client" ? "client" : false;

  return (
    <Box
      ref={topSectionRef}
      sx={{ p: { xs: 2, md: 4 }, background: "#f7f5fb", minHeight: "100vh" }}
    >
      {/* Header Section */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(90deg, #5b2c72 0%, #7b1fa2 100%)",
          color: "white",
          overflow: "hidden",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.5, sm: 2 },
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              <Business sx={{ fontSize: { xs: 24, sm: 30 } }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="white"
                sx={{
                  fontSize: { xs: "1.3rem", sm: "1.8rem", md: "2.125rem" },
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {empresa}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {clientName}
              </Typography>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              width: { xs: "100%", md: "auto" },
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            {!voltar ? (
              <Chip
                label="Voltar"
                color="default"
                onClick={() => {
                  navigate(-1); // Navigate back to the previous page
                }}
                icon={<ArrowBack color="inherit" />}
                size="small"
                sx={{
                  cursor: "pointer",
                  backgroundColor: "rgba(255,255,255,0.14)",
                  color: "white",
                  fontWeight: 600,
                }}
              />
            ) : null}
            <Chip
              label={chatLocked ? "Chat ocupado" : "Chat"}
              color="default"
              onClick={goToChat}
              icon={
                <Badge
                  color="error"
                  badgeContent={chatUnreadCount}
                  invisible={chatUnreadCount <= 0}
                >
                  <Chat color="inherit" />
                </Badge>
              }
              disabled={chatLocked || chatLockLoading}
              size="small"
              sx={{
                cursor: "pointer",
                backgroundColor: "rgba(255,255,255,0.14)",
                color: "white",
                fontWeight: 600,
                opacity: chatLocked || chatLockLoading ? 0.6 : 1,
              }}
            />
            <Chip
              label="Sair"
              color="default"
              onClick={() => {
                auth.logout();
                navigate("/");
              }}
              icon={<Logout color="inherit" />}
              size="small"
              sx={{
                cursor: "pointer",
                backgroundColor: "rgba(255,255,255,0.14)",
                color: "white",
                fontWeight: 600,
              }}
            />
          </Stack>
        </CardContent>
      </Card>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          mb: 3,
          border: "1px solid #ebe5f4",
          backgroundColor: "#ffffff",
        }}
      >
        <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 } }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<Home fontSize="small" />}
              label="Principal"
              onClick={() => scrollToSection("home")}
              clickable
              color={activeSection === "home" ? "secondary" : "default"}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<Handyman fontSize="small" />}
              label="Serviços"
              onClick={() => scrollToSection("services")}
              clickable
              color={activeSection === "services" ? "secondary" : "default"}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<CalendarToday fontSize="small" />}
              label="Calendário"
              onClick={() => scrollToSection("events")}
              clickable
              color={activeSection === "events" ? "secondary" : "default"}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<Archive fontSize="small" />}
              label="Arquivo"
              onClick={() => scrollToSection("files")}
              clickable
              color={activeSection === "files" ? "secondary" : "default"}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </CardContent>
      </Card>
      {/* ######################################## */}
      <Grid container spacing={3}>
        {activeSection !== "events" ? (
          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>
              {activeSection === "home" ? (
                <>
                  <Documents
                    clientId={principalClientId}
                    companyId={resolvedCompanyId}
                  />
                  <Contract
                    clientId={principalClientId}
                    companyId={resolvedCompanyId}
                  />
                </>
              ) : null}
              {activeSection === "files" ? (
                <Arquivo
                  clientId={principalClientId}
                  companyId={resolvedCompanyId}
                />
              ) : null}
              {activeSection === "services" ? (
                <Box ref={servicesSectionRef}>
                  <Servicos
                    clientId={principalClientId}
                    companyId={resolvedCompanyId}
                  />
                </Box>
              ) : null}
            </Stack>
          </Grid>
        ) : null}
        {activeSection === "events" || activeSection === "home" ? (
          <>
            <Grid
              item
              xs={12}
              ref={eventsSectionRef}
              sx={{ p: 0, m: 0, height: 0 }}
            />
            <Events
              mode={activeSection === "home" ? "compact" : "full"}
              companyId={resolvedCompanyId}
              clientId={principalClientId}
            />
          </>
        ) : null}
      </Grid>
      <Snackbar
        open={chatAlertOpen}
        autoHideDuration={4000}
        onClose={() => setChatAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setChatAlertOpen(false)}
          sx={{ width: "100%" }}
        >
          {chatLockMessage || "Chat em uso por outro utilizador deste lado."}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientDashboardScreen;
